import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { Annuaire, Annuaires, ColumnReference, FicheDAnnuaire } from '../views/contract.js';
import { cleDeJeu } from '../views/project.js';
import type { IdsVoulus } from '../views/project.js';
import { RAISON_LIENS, formaterNombre } from './liens.js';
import type { ArgumentsDeListe, LiensPort } from './liens.js';
import { annuairesLus, poserLesAnnuaires } from './annuaires-lus.js';
import {
  RAISON_PERSONNES,
  TYPE_ANNUAIRE_PAR_UID,
  estReferenceDePersonnes,
  fichesDAnnuaireDePersonnes,
} from './personnes.js';
import type { PersonneVue, PersonnesPort } from './personnes.js';

/**
 * L'ANNUAIRE D'UN JEU : le titre des fiches qu'une colonne de REFERENCE
 * designe, charge UNE fois pour tout le monde.
 *
 * A QUOI IL SERT. Une colonne de reference stocke un id (« un lien stocke un
 * id, jamais un libelle recopie ») : sans annuaire, la ligne d'une
 * intervention affiche `m1RxQ2...` la ou l'utilisateur attend « Sony FX9 ».
 * L'annuaire est la table de correspondance, et c'est lui qui ferme le fork ou
 * chaque ecran chargeait une collection entiere pour resoudre des noms.
 *
 * UN MAGASIN DE MODULE, comme le cache des modules de vue
 * (`views/useViewModule.ts`), et pour la meme raison : deux ecrans, une carte
 * liee et une fiche peuvent viser le MEME jeu dans le meme document. Un cache
 * par composant chargerait le meme annuaire trois fois, et trois copies de la
 * meme regle de peremption auraient diverge au premier ajustement.
 *
 * IL LIT PAR `list`, JAMAIS PAR `get`. Lire un document ABSENT par `getDoc`
 * rend `permission-denied` et non un snapshot vide (les rules evaluent
 * `resource.data.orgId` sur un `resource` nul, fait deja verifie et ecrit dans
 * `packages/sdk/src/list-query.ts`) : un id orphelin ferait donc remonter une
 * erreur de DROITS, c'est-a-dire le pire message possible pour un fait qui n'en
 * est pas un. Il lit le jeu PAR PAGES, jusqu'au plafond (`listerParPages`), et
 * un id qui ne s'y trouve pas ne revient tout simplement pas.
 *
 * CE QUI MANQUE AUX PAGES SE LIT A LA DEMANDE (`souhaiterTitres`), et
 * seulement au-dela du plafond de la lecture par pages : une fiche liee existe
 * alors sans se trouver dans l'annuaire, et la cellule montrerait son id nu.
 * Sous le plafond, l'annuaire est complet et rien ne part. Les surfaces qui
 * projettent des lignes disent donc les ids qu'elles montrent
 * (`idsDesReferences`) ; ceux que la fenetre ne couvre pas sont lus PAR LOT de
 * trente (`ListOptions.ids` du SDK, la borne de `in`), jamais un `get` : une
 * LISTE cadree par l'org rend moins de lignes qu'on n'a demande d'ids, un `get`
 * d'un document absent rend `permission-denied`. Ces fiches vivent dans le meme
 * magasin que la fenetre et partent avec elle a l'invalidation.
 *
 * IL NE SE PERIME PAS AU TEMPS. La seule invalidation est le RECHARGEMENT de
 * la porte (`invaliderAnnuaires`, appele par `reloadPort`), donc apres chaque
 * ecriture faite depuis l'ecran. Un cache qui se rafraichit tout seul rendrait
 * le meme ecran different d'une seconde a l'autre sans que rien ne l'explique.
 */

/**
 * LE PLAFOND, ECRIT UNE FOIS : le nombre de fiches ACTIVES qu'un annuaire lit
 * au plus, donc celles qu'un champ de lien peut proposer. Les ARCHIVES lues en
 * route (il faut bien nommer une reference qui en designe une) ne le consomment
 * pas : 8 501 archives en tete du jeu cachaient la derniere des 1 500 fiches
 * actives (relecture Astra du 02/11/2026). La lecture totale a sa propre borne
 * (`PLAFOND_LECTURE_ANNUAIRE`).
 *
 * LE SDK NE CONNAIT PAS `in` (une egalite, et rien d'autre :
 * `packages/sdk/src/list-query.ts`), et Firestore borne de toute facon `in` a
 * trente valeurs : un annuaire ne peut donc pas demander « ces quarante ids
 * la ». Il charge le jeu entier, page apres page, et au-dela de ce plafond une
 * fiche existe sans se trouver. C'est une limite assumee, pas un defaut a
 * corriger en douce : Firestore n'a pas de plein texte, et la seule sortie
 * serait un index externe, qui est un autre chantier.
 *
 * DIX MILLE, ET NON PLUS CINQ CENTS (02/11/2026). A cinq cents, la fiche d'une
 * ligne qui vise 1 500 produits ne proposait que les cinq cents premiers ids (la
 * recherche « 01400 » ne trouvait rien), et le tableau des lignes montrait
 * `ef-p-00501` la ou l'on attendait un nom. Un champ de lien doit pouvoir
 * choisir N'IMPORTE QUELLE fiche du jeu, et nommer chacune. Dix mille est la
 * borne que le chantier `ecran-fluide` a mesuree pour le mode client (la porte
 * lit desormais son jeu par pages, sans plafond) : au-dela, un jeu releve du
 * mode serveur, et ce n'est plus l'annuaire qu'il faut agrandir. Le plafond
 * atteint, l'annuaire n'est pas `complet`, et le selecteur DIT que sa recherche
 * est partielle (`ChoixDeFicheLiee.partiel`).
 */
export const PLAFOND_ANNUAIRE = 10_000;

/**
 * LA BORNE DE LA LECTURE ENTIERE, archives comprises : quatre fois le plafond.
 * Un jeu fait surtout d'archives se lit donc jusqu'a ses fiches actives, sans
 * qu'un jeu demesure ne remplisse l'onglet. Atteinte, l'annuaire n'est pas
 * `complet`, comme au plafond.
 */
export const PLAFOND_LECTURE_ANNUAIRE = 4 * PLAFOND_ANNUAIRE;

/**
 * CE QUE DIT UN CHOIX DE FICHE LIEE dont la lecture s'est arretee au plafond :
 * la portee de la recherche, sous son champ (selecteur d'un champ de lien,
 * fenetre de rattachement d'une carte liee). Un libelle, pas une phrase (G26).
 */
export const RECHERCHE_PARTIELLE = `Recherche sur les ${formaterNombre(PLAFOND_ANNUAIRE)} premières fiches actives`;

/**
 * LA TAILLE D'UNE PAGE : le plafond d'une page du SDK (`MAX_LIST_LIMIT`,
 * `packages/sdk/src/list-query.ts`), RECOPIE ET NON IMPORTE. Le design system ne
 * depend pas de la couche donnees (meme raison que `MAX_CLIENT_WINDOW`,
 * `views/useScreen.ts`). Plus grande, le SDK leverait ; plus petite, le meme jeu
 * couterait plus d'allers-retours.
 */
export const PAGE_ANNUAIRE = 1000;

/** Ce que `listerParPages` rend : les fiches lues, et si elles couvrent le jeu. */
export interface LectureParPages {
  fiches: Array<Record<string, unknown>>;
  /**
   * LA DERNIERE PAGE LUE N'ETAIT PAS PLEINE : c'est le seul fait qui dise « il
   * n'y a rien de plus ». Faux au plafond, et faux quand le garde-fou arrete la
   * lecture.
   */
  complet: boolean;
}

/**
 * LE JEU PAR PAGES, jusqu'au plafond : des pages de `PAGE_ANNUAIRE` fiches au
 * plus, enchainees par le curseur `after` (l'id de la derniere fiche de la page
 * precedente), tant que la page revient PLEINE.
 *
 * LE GARDE-FOU CONTRE UN HOTE QUI IGNORE `after`. Un hote d'avant ce curseur
 * (ou un banc d'essai) rendrait la PREMIERE page a chaque appel, et la boucle
 * relirait le meme millier jusqu'au plafond, dix fois, pour le meme resultat.
 * Une page qui n'apporte AUCUN id nouveau, ou dont le dernier id est celui de la
 * precedente, arrete donc la lecture, et `complet` reste faux : on ne sait pas
 * ce qui suit, on ne le pretend pas.
 *
 * LES FICHES SONT RENDUES TELLES QUE LUES : ce sont les appelants qui indexent
 * par id (l'annuaire, la fenetre de choix), et un doublon s'y ecrase de
 * lui-meme.
 *
 * FONCTION EXPORTEE EXPRES : la pagination est le seul endroit ou une erreur ne
 * se voit qu'a la mille et unieme fiche, et un test la tient sans React.
 */
export async function listerParPages(
  port: LiensPort,
  family: string,
  args: Omit<ArgumentsDeListe, 'limit' | 'after'>,
  plafond: number = PLAFOND_ANNUAIRE,
  /**
   * CE QUI COMPTE DANS LE PLAFOND. Par defaut : les fiches ACTIVES quand la
   * lecture prend les archives (`archived: 'all'`, un annuaire), toute fiche
   * sinon. Les fiches qui ne comptent pas se lisent en plus, jusqu'a
   * `plafondDeLecture` : des archives en tete du jeu ne cachent plus une fiche
   * active (relecture Astra du 02/11/2026).
   */
  compte: ((fiche: Record<string, unknown>) => boolean) | undefined = args.archived === 'all'
    ? (fiche) => fiche['archivedAt'] == null
    : undefined,
  plafondDeLecture: number = compte === undefined ? plafond : PLAFOND_LECTURE_ANNUAIRE,
): Promise<LectureParPages> {
  const fiches: Array<Record<string, unknown>> = [];
  const vus = new Set<string>();
  let comptees = 0;
  let after: string | undefined;
  for (;;) {
    /* Sans predicat, la page ne demande que le reste jusqu'au plafond ; avec,
       on ne sait pas d'avance combien de fiches compteront. */
    const reste = compte === undefined ? plafond - comptees : PAGE_ANNUAIRE;
    const limit = Math.min(PAGE_ANNUAIRE, reste, plafondDeLecture - fiches.length);
    if (limit <= 0 || comptees >= plafond) return { fiches, complet: false };
    const page = await port.list(family, {
      ...args,
      limit,
      ...(after === undefined ? {} : { after }),
    });
    let nouveaux = 0;
    for (const fiche of page) {
      fiches.push(fiche);
      if (compte === undefined || compte(fiche)) comptees += 1;
      const id = fiche['id'];
      if (typeof id === 'string' && id !== '' && !vus.has(id)) {
        vus.add(id);
        nouveaux += 1;
      }
    }
    /* LE NOMBRE DE LIGNES, PAS D'IDS : une fiche sans id ne fait pas croire a
       une page a moitie vide, donc a un jeu fini. */
    if (page.length < limit) return { fiches, complet: true };
    const dernier = page.at(-1)?.['id'];
    if (nouveaux === 0 || typeof dernier !== 'string' || dernier === '' || dernier === after) {
      return { fiches, complet: false };
    }
    after = dernier;
  }
}

/** Un annuaire vide, partage : une identite stable evite des rendus pour rien. */
const ANNUAIRE_VIDE: Annuaire = { fiches: new Map(), complet: false };

/**
 * CE QUE LE MAGASIN SAIT D'UN JEU.
 *
 * `revision` EST LA REVISION SOUS LAQUELLE CETTE ENTREE A ETE DEMANDEE, et
 * c'est elle qui tient les deux bords : elle dit qu'une lecture est deja
 * partie pour la revision courante (donc deux hooks montes ensemble ne
 * chargent pas deux fois), et elle fait tomber une reponse PERIMEE, qui ne doit
 * jamais remplacer une fraiche.
 *
 * `complet` NE VIT PLUS ICI, IL VIT DANS L'ANNUAIRE (`Annuaire.complet`) : la
 * projection et la fiche doivent dire la MEME chose d'un id inconnu, et elles
 * ne le peuvent que si le fait voyage avec les fiches, dans le meme objet.
 */
interface EtatAnnuaire {
  revision: number;
  statut: 'loading' | 'ready' | 'error';
  annuaire: Annuaire;
  error?: Error;
  /**
   * LA REVISION DONT LA FENETRE A ETE LUE jusqu'au bout. `statut: 'ready'` ne
   * suffit pas a le dire : pendant une relecture le magasin garde l'annuaire
   * d'avant et reste `ready`. Les titres lus a la demande ne partent qu'une fois
   * la fenetre de la revision courante revenue (`lue === REVISION`), sinon ils
   * seraient calcules contre l'ancienne.
   */
  lue?: number;
}

/** Ce qu'un lecteur a le droit de savoir d'un annuaire, sans son contenu. */
export interface EtatDAnnuaire {
  statut: 'loading' | 'ready' | 'error';
  /** La fenetre chargee couvre-t-elle le jeu entier ? */
  complet: boolean;
  /** La raison de l'echec, quand il y en a un. */
  error?: Error;
}

const MAGASIN = new Map<string, EtatAnnuaire>();
const ABONNES = new Set<() => void>();

/** La revision COURANTE. Elle ne monte que par `invaliderAnnuaires`. */
let REVISION = 0;

/**
 * LA CARTE RENDUE AUX LECTEURS, memoisee.
 *
 * `useSyncExternalStore` compare l'instantane par IDENTITE : en fabriquer une
 * neuve a chaque lecture ferait boucler le rendu. Elle est donc reconstruite au
 * seul moment ou le magasin change, et rendue telle quelle le reste du temps.
 *
 * ELLE NE PORTE QUE CE QUI EST PRET. Une clef absente et un annuaire vide ne
 * disent pas la meme chose (contrat `Annuaires`) : absente, l'annuaire n'est
 * pas encore charge et la cellule montre l'id nu ; presente et vide, il l'est
 * et ne connait pas cet id.
 */
function reconstruireInstantane(): void {
  const carte = new Map<string, Annuaire>();
  for (const [cle, etat] of MAGASIN) {
    if (etat.statut === 'ready') carte.set(cle, etat.annuaire);
  }
  /* L'INSTANTANE VIT DANS UN MODULE FEUILLE (`annuaires-lus.ts`) : les champs
     `lecture` de `views/screens.ts` le lisent sans importer ce magasin. */
  poserLesAnnuaires(carte);
}

/**
 * ON PREVIENT APRES AVOIR RECONSTRUIT, et on reconstruit a CHAQUE changement,
 * y compris un echec : l'etat d'un annuaire est lu a cote de son contenu (voir
 * `etatAnnuaire`), et une identite qui ne bouge pas laisserait la fiche
 * afficher « chargement » sur un annuaire deja tombe.
 */
function prevenir(): void {
  reconstruireInstantane();
  for (const abonne of ABONNES) abonne();
}

function sAbonner(abonne: () => void): () => void {
  ABONNES.add(abonne);
  return () => {
    ABONNES.delete(abonne);
  };
}

function lireInstantane(): Annuaires {
  return annuairesLus();
}

/** La revision courante : ce qui fait repartir les chargements d'un hook monte. */
function lireRevision(): number {
  return REVISION;
}

/**
 * LES ANNUAIRES PRETS, hors React : le meme instantane que le hook rend. Sert au
 * banc d'essai, aux tests, et a tout ce qui projette des lignes sans etre un
 * composant.
 */
export function annuairesCharges(): Annuaires {
  return annuairesLus();
}

/**
 * TOUS LES ANNUAIRES SONT PERIMES.
 *
 * Appelee par le rechargement de la PORTE (`reloadPort`, `views/useScreen.ts`),
 * donc apres chaque ecriture faite depuis l'ecran, un LOT compris : le nom d'une
 * fiche liee qu'on vient de renommer doit suivre. Elle l'etait par `reload()`
 * jusqu'a la relecture du 09/09/2026, et une modification groupee passait donc
 * a cote (elle recharge sans vider la selection, par `reloadPort`).
 *
 * ELLE NE VIDE PAS LE MAGASIN, elle monte la REVISION : les noms deja resolus
 * restent a l'ecran pendant que la lecture repart. Vider ferait clignoter en
 * ids nus toutes les cellules de reference a chaque rechargement, c'est-a-dire
 * a chaque case modifiee.
 */
export function invaliderAnnuaires(): void {
  REVISION += 1;
  prevenir();
}

/**
 * L'ETAT D'UN ANNUAIRE, lu par sa clef de jeu (`cleDeJeu`).
 *
 * POURQUOI CE N'EST PAS UN HOOK. Son seul lecteur est la FICHE, qui doit dire
 * « Lecture impossible » plutot que de proposer une liste vide (G16), et une
 * vue ne prend que des props : lui passer un hook de plus ferait entrer un
 * contexte dans le contrat des vues. C'est une lecture PURE du magasin, et la
 * fiche est re-rendue de toute facon des que le magasin bouge, puisque la carte
 * des annuaires qu'elle recoit change d'identite au meme instant.
 *
 * `undefined` = personne n'a encore demande cet annuaire.
 */
export function etatAnnuaire(cle: string): EtatDAnnuaire | undefined {
  const etat = MAGASIN.get(cle);
  if (etat === undefined) return undefined;
  return {
    statut: etat.statut,
    complet: etat.annuaire.complet,
    ...(etat.error === undefined ? {} : { error: etat.error }),
  };
}

/**
 * L'ANNUAIRE D'UNE REFERENCE, DEMANDE UNE FOIS PAR REVISION.
 *
 * `titleOf` et `subtitleOf` viennent de la porte REFERENCEE (assembles par
 * `views/screens.ts`) : personne n'invente un second titre, l'annuaire affiche
 * celui que cette porte affiche dans ses propres listes.
 *
 * A UNE PORTE PRES, ET IL FAUT LE DIRE. Le magasin est indexe par JEU, pas par
 * porte : deux portes qui montrent le meme jeu partagent le meme annuaire, et
 * c'est la PREMIERE demande qui fixe le titre pour tout le monde. Un annuaire
 * par porte doublerait la lecture pour deux titres qui, dans la vraie vie, sont
 * les memes ; c'est le prix assume d'un cache partage, et l'assemblage tranche
 * deja dans le meme sens (la premiere porte declaree qui porte un jeu gagne,
 * `liensEntrants`).
 *
 * ELLE EST EXPORTEE, ET C'EST LE MAGASIN SANS REACT : `useAnnuaires` n'est que
 * la liaison React par-dessus (elle abonne, elle rend l'instantane, elle
 * appelle ceci). Les bords qui coutent vraiment (deux hooks qui demandent le
 * meme jeu, une reponse perimee qui revient apres une invalidation, une page
 * pleine, un echec) se tiennent ici, et un test ne peut les atteindre que sans
 * DOM.
 */
export function demanderAnnuaire(
  port: LiensPort | null,
  reference: ColumnReference,
  /**
   * LE PORT DES PERSONNES, pour une reference qui vise leur annuaire
   * (`estReferenceDePersonnes`). Facultatif : les appelants d'avant ce
   * chantier ne demandent que des fiches.
   */
  personnes: PersonnesPort | null = null,
): void {
  /* L'ANNUAIRE DES PERSONNES N'EST PAS UNE FAMILLE DU SDK (comme les membres) :
     il ne se lit pas par `LiensPort.list`, il a son port. Le reste du magasin
     (revision, reponse perimee, echec) est le meme, et c'est pour ca qu'il
     passe par ici plutot que par un second magasin. */
  if (estReferenceDePersonnes(reference)) {
    if (personnes === null) {
      marquerSansPort(reference, SANS_PORT_PERSONNES);
      return;
    }
    demanderAnnuaireDePersonnes(personnes, reference);
    return;
  }
  if (port === null) {
    marquerSansPort(reference, SANS_PORT);
    return;
  }
  const cle = cleDeJeu(reference.family, reference.type);
  const courant = MAGASIN.get(cle);
  // Deja demande pour CETTE revision : rien a refaire. C'est ce qui empeche
  // deux hooks montes ensemble de charger deux fois le meme jeu.
  if (courant !== undefined && courant.revision === REVISION) return;
  const revision = REVISION;
  MAGASIN.set(cle, {
    revision,
    // On GARDE ce qu'on avait pendant la relecture : le statut ne retombe a
    // `loading` que la toute premiere fois. Sinon les noms disparaitraient a
    // chaque rechargement, le temps d'un aller-retour serveur.
    statut: courant?.statut === 'ready' ? 'ready' : 'loading',
    annuaire: courant?.annuaire ?? ANNUAIRE_VIDE,
    ...(courant?.lue === undefined ? {} : { lue: courant.lue }),
  });
  prevenir();

  /* PAR PAGES, ET UNE SEULE MISE A JOUR DU MAGASIN, A LA FIN : publier page par
     page ferait rendre tous les ecrans abonnes dix fois pour un seul annuaire,
     et une cellule passerait de l'id nu au nom en cours de route. */
  void listerParPages(port, reference.family, {
      ...(reference.type === undefined ? {} : { type: reference.type }),
      /* AUCUN `where` : un annuaire resout les noms de TOUTES les fiches qu'une
         colonne peut designer, donc il n'a rien a cadrer d'autre que le
         sous-type. Une egalite y retiendrait justement les fiches deja liees,
         c'est-a-dire l'inverse de ce qu'un choix demande. */
      /* LES ARCHIVES COMPRISES, ET C'EST LE POINT. Une intervention peut
         pointer un materiel archive : sans lui, l'annuaire ne le connaitrait
         pas, et la cellule dirait « Supprimee ou introuvable » sur une fiche qui
         existe toujours. Une archive s'AFFICHE (attenuee, avec son mot) ; ce
         qu'elle ne fait plus, c'est se PROPOSER dans un choix, et ca se decide
         a la surface, pas ici. */
      archived: 'all',
    },
    /* Le plafond ne compte que les fiches ACTIVES (le defaut de
       `listerParPages` pour une lecture archives comprises) : ce sont elles
       qu'un choix propose. */
    )
    .then(
      ({ fiches, complet }) => {
        /* UNE REPONSE PERIMEE NE REMPLACE JAMAIS UNE FRAICHE. Entre le depart
           de cette lecture et son retour, une invalidation a pu partir, et sa
           lecture a elle a pu revenir la premiere : ecrire ici reposerait les
           noms d'AVANT l'ecriture qu'on vient de faire. */
        if (MAGASIN.get(cle)?.revision !== revision) return;
        const resolues = new Map<string, FicheDAnnuaire>();
        for (const fiche of fiches) {
          const id = fiche['id'];
          if (typeof id !== 'string' || id === '') continue;
          resolues.set(id, ficheDAnnuaire(reference, fiche));
        }
        MAGASIN.set(cle, {
          revision,
          statut: 'ready',
          lue: revision,
          annuaire: {
            fiches: resolues,
            /* « COMPLET » VEUT DIRE « LA DERNIERE PAGE N'EST PAS PLEINE », et
               c'est le seul fait qu'on connaisse vraiment : une page pleine
               peut cacher une fiche de plus, une page a moitie vide, non. Il se
               lit sur les LIGNES et non sur la taille de l'annuaire
               (`listerParPages`) : deux fiches sans id tomberaient de la carte
               et feraient croire a une fenetre non pleine. Au plafond, il est
               faux, et la fiche ne conclut donc plus « introuvable ». */
            complet,
          },
        });
        /* LES TITRES DEMANDES QUE CETTE FENETRE NE COUVRE PAS partent ICI, avant
           la notification : l'annuaire est publie avec ses ids deja « en
           attente », et la cellule ne passe jamais par l'id nu. */
        const complement = COMPLEMENTS.get(cle);
        if (complement !== undefined) complement.fenetre = resolues;
        lireLesTitres(cle, port);
        recomposer(cle);
        prevenir();
      },
      (cause: unknown) => {
        if (MAGASIN.get(cle)?.revision !== revision) return;
        const error = cause instanceof Error ? cause : new Error(String(cause));
        console.error(`[taki/liens] Annuaire du jeu "${cle}" illisible.`, error);
        /* UN ECHEC LAISSE L'ANNUAIRE VIDE ET `complet` FAUX : rien ne se resout,
           et surtout personne ne conclut « fiche introuvable » sur une lecture
           qui n'a pas eu lieu. La fiche, elle, le DIT (G16). */
        MAGASIN.set(cle, { revision, statut: 'error', annuaire: ANNUAIRE_VIDE, error });
        prevenir();
      },
    );
}

/**
 * CE QU'UNE FICHE DEVIENT DANS L'ANNUAIRE : son titre, tel que la porte
 * referencee l'affiche, et son etat. UNE seule fabrique pour la fenetre et pour
 * les titres lus a la demande : deux copies auraient donne deux titres a la meme
 * fiche selon qu'elle est dans la fenetre ou non.
 */
function ficheDAnnuaire(reference: ColumnReference, fiche: Record<string, unknown>): FicheDAnnuaire {
  const subtitle = reference.subtitleOf?.(fiche);
  const type = fiche['type'];
  return {
    title: reference.titleOf(fiche),
    ...(subtitle === undefined || subtitle === '' ? {} : { subtitle }),
    /* Le sous-type du DOCUMENT, pour qu'une reference sans type sache
       quelle porte typee ouvre cette fiche (`FicheDAnnuaire.type`). */
    ...(typeof type === 'string' && type !== '' ? { type } : {}),
    /* L'ARCHIVE SE LIT SUR LE DOCUMENT, jamais sur une declaration :
       `archivedAt` est le champ standard des trois etats (actif,
       archive, supprime), pose par le SDK (AGENTS.md section 7).
       ET IL NE VAUT JAMAIS POUR UNE SUPPRIMEE : celle-ci porte aussi
       `archivedAt`, donc `archived: 'all'` la ramene du serveur, mais le
       SDK la retire cote client AVANT qu'on arrive ici (`runList`). Un
       annuaire ne contient donc que des fiches vivantes ou archivees, et
       c'est ce qui autorise la cellule a dire « Supprimee ou
       introuvable » de tout id qu'il ne connait pas. */
    archive: fiche['archivedAt'] != null,
    /* LA FICHE BRUTE, pour les champs `lecture` : le champ d'une fiche
       liee se lit ici, a l'affichage, sans second chargement. Vaut aussi
       pour un titre lu a la demande (`lireLesTitres`). */
    valeurs: fiche,
    /* LE DOCUMENT BRUT, pour la condition des choix d'un champ
       (`ColumnReference.choixPermis`) : elle lit les champs de la
       fiche visee, et l'annuaire est la seule lecture qu'on en a. */
    document: fiche,
  };
}

/* ------------------------------------------------------------------ */
/* Les titres lus a la demande, hors de la fenetre                     */
/* ------------------------------------------------------------------ */

/** La borne de `in` chez Firestore, donc de `ListOptions.ids` (`@taki/sdk`). */
const TAILLE_DE_LOT = 30;

/**
 * LE PLUS D'IDS LUS A LA DEMANDE, PAR JEU ET PAR REVISION. Vingt lots au plus :
 * un ecran qui montre des milliers de lignes distinctes ne lance pas des
 * centaines de requetes d'un coup. Au-dela, les ids restent nus, comme avant
 * (la limite est ecrite, pas cachee).
 */
export const PLAFOND_TITRES_A_LA_DEMANDE = 20 * TAILLE_DE_LOT;

/**
 * CE QUE LE MAGASIN SAIT DES TITRES A LIRE POUR UN JEU.
 *
 * `voulus` ne fait que grossir pendant la vie de l'ecran (un id vu reste voulu :
 * c'est le cache de session, sans requete par ligne). `demandes` est ce qui est
 * deja parti POUR LA REVISION COURANTE (en vol ou revenu, trouve ou non), donc
 * un id introuvable n'est pas redemande a chaque rendu. `trouvees` survit a
 * l'invalidation (les noms restent a l'ecran pendant la relecture, comme ceux de
 * la fenetre) et est ecrasee par la relecture.
 */
interface Complement {
  reference: ColumnReference;
  voulus: Set<string>;
  revision: number;
  demandes: Set<string>;
  enVol: Set<string>;
  trouvees: Map<string, FicheDAnnuaire>;
  /** La fenetre de la revision courante, sans les titres a la demande. */
  fenetre: ReadonlyMap<string, FicheDAnnuaire>;
}

const COMPLEMENTS = new Map<string, Complement>();

/**
 * L'ANNUAIRE PUBLIE = la fenetre + les titres lus a la demande + ce qui est en
 * attente. Ne notifie pas : l'appelant le fait, une seule fois.
 */
function recomposer(cle: string): void {
  const etat = MAGASIN.get(cle);
  const complement = COMPLEMENTS.get(cle);
  if (etat === undefined || complement === undefined || etat.statut !== 'ready') return;
  const fiches = new Map(complement.fenetre);
  /* UNE FENETRE COMPLETE fait autorite : un id qu'elle ne porte pas est une
     fiche supprimee, son ancien titre lu a la demande ne se publie plus. */
  if (!etat.annuaire.complet) for (const [id, fiche] of complement.trouvees) if (!fiches.has(id)) fiches.set(id, fiche);
  MAGASIN.set(cle, {
    ...etat,
    annuaire: {
      fiches,
      complet: etat.annuaire.complet,
      ...(complement.enVol.size === 0 ? {} : { enAttente: new Set(complement.enVol) }),
    },
  });
}

/**
 * LIT PAR LOT LES TITRES VOULUS QUE LA FENETRE NE COUVRE PAS.
 *
 * Ne part que si la fenetre de la revision courante est revenue (`lue`) et
 * qu'elle n'est pas complete (sinon un id absent est une fiche supprimee, pas
 * une fiche hors fenetre). Les ids partent dans `enVol` AVANT la requete : la
 * recomposition qui suit les publie « en attente », et la cellule reste vide au
 * lieu de montrer l'id. Un echec laisse les ids nus (comme avant) et ne
 * redemande pas en boucle.
 */
function lireLesTitres(cle: string, port: LiensPort): void {
  const complement = COMPLEMENTS.get(cle);
  const etat = MAGASIN.get(cle);
  if (complement === undefined || etat === undefined) return;
  if (etat.lue !== REVISION || etat.revision !== REVISION) return;
  /* LA REMISE A ZERO PAR REVISION PASSE AVANT « COMPLET » : une lecture partie
     sous la revision d'avant est perimee (`perime`) et ne videra jamais
     `enVol`. Si la fenetre de la nouvelle revision revient COMPLETE, rien ne
     repart et ces ids resteraient « en attente » pour toujours, cases vides au
     lieu de l'id nu ou de « Supprimee ou introuvable ». */
  if (complement.revision !== REVISION) {
    complement.revision = REVISION;
    complement.demandes = new Set();
    complement.enVol = new Set();
  }
  if (etat.annuaire.complet) return;
  const manquants = [...complement.voulus]
    .filter((id) => !complement.fenetre.has(id) && !complement.demandes.has(id))
    .slice(0, Math.max(0, PLAFOND_TITRES_A_LA_DEMANDE - complement.demandes.size));
  if (manquants.length === 0) return;
  const revision = REVISION;
  const { reference } = complement;
  for (const id of manquants) {
    complement.demandes.add(id);
    complement.enVol.add(id);
  }
  for (let debut = 0; debut < manquants.length; debut += TAILLE_DE_LOT) {
    const lot = manquants.slice(debut, debut + TAILLE_DE_LOT);
    const perime = () => complement.revision !== revision || MAGASIN.get(cle)?.revision !== revision;
    void port
      .list(reference.family, {
        ...(reference.type === undefined ? {} : { type: reference.type }),
        ids: lot,
        limit: lot.length,
        /* LES ARCHIVES COMPRISES, pour la meme raison que la fenetre. */
        archived: 'all',
      })
      .then(
        (fiches) => {
          if (perime()) return;
          const rendus = new Set<string>();
          for (const fiche of fiches) {
            const id = fiche['id'];
            if (typeof id === 'string' && id !== '') {
              rendus.add(id);
              complement.trouvees.set(id, ficheDAnnuaire(reference, fiche));
            }
          }
          /* UN ID DEMANDE ET NON RENDU n'existe plus : son titre d'une revision
             precedente ne doit pas survivre a la relecture. */
          for (const id of lot) {
            complement.enVol.delete(id);
            if (!rendus.has(id)) complement.trouvees.delete(id);
          }
          recomposer(cle);
          prevenir();
        },
        (cause: unknown) => {
          if (perime()) return;
          console.error(`[taki/liens] Titres du jeu "${cle}" illisibles.`, cause);
          for (const id of lot) complement.enVol.delete(id);
          recomposer(cle);
          prevenir();
        },
      );
  }
}

/**
 * LES SURFACES DISENT LES IDS QU'ELLES MONTRENT, l'annuaire nomme ceux que sa
 * fenetre ne couvre pas.
 *
 * C'est le seul moyen de nommer une fiche au-dela du plafond sans charger tout
 * le jeu : on lit les ids qu'on VOIT, par lots, une fois pour la session de
 * l'ecran (un id deja voulu ne repart pas). Le plafond (`PLAFOND_ANNUAIRE`)
 * reste celui de la fenetre ; ceci n'y change rien pour les ecrans dont tous
 * les ids tiennent dedans.
 */
export function souhaiterTitres(
  port: LiensPort | null,
  reference: ColumnReference,
  ids: Iterable<string>,
): void {
  if (port === null || estReferenceDePersonnes(reference) || reference.repli !== undefined) return;
  const cle = cleDeJeu(reference.family, reference.type);
  let complement = COMPLEMENTS.get(cle);
  if (complement === undefined) {
    const etat = MAGASIN.get(cle);
    complement = {
      reference,
      voulus: new Set(),
      revision: -1,
      demandes: new Set(),
      enVol: new Set(),
      trouvees: new Map(),
      fenetre: etat?.lue === REVISION ? etat.annuaire.fiches : new Map(),
    };
    COMPLEMENTS.set(cle, complement);
  }
  complement.reference = reference;
  let nouveau = false;
  for (const id of ids) {
    if (id !== '' && !complement.voulus.has(id)) {
      complement.voulus.add(id);
      nouveau = true;
    }
  }
  if (!nouveau) return;
  const etat = MAGASIN.get(cle);
  // Pas de fenetre lue pour cette revision : c'est sa lecture qui lancera ceux-ci.
  if (etat?.lue !== REVISION) return;
  lireLesTitres(cle, port);
  recomposer(cle);
  prevenir();
}

/**
 * LA LECTURE DE L'ANNUAIRE DES PERSONNES, PARTAGEE PAR SES DEUX CLEFS.
 *
 * `personnes` (par `personId`) et `personnes/uid` (par uid, pour la trace)
 * sont deux annuaires du magasin, mais UNE seule collection : un ecran qui
 * montre « Intervenant » et « Cree par » ne la lit qu'une fois par revision
 * et par port. Un port neuf (l'hote a relu la fiche membre) relit.
 */
let LECTURE_PERSONNES: {
  revision: number;
  port: PersonnesPort;
  promesse: Promise<PersonneVue[]>;
} | null = null;

function lirePersonnes(port: PersonnesPort, revision: number): Promise<PersonneVue[]> {
  if (LECTURE_PERSONNES !== null && LECTURE_PERSONNES.revision === revision && LECTURE_PERSONNES.port === port) {
    return LECTURE_PERSONNES.promesse;
  }
  const promesse = port.lister();
  LECTURE_PERSONNES = { revision, port, promesse };
  return promesse;
}

/**
 * L'ANNUAIRE DES PERSONNES, DEMANDE UNE FOIS PAR REVISION : le meme contrat que
 * celui d'une famille (juste au-dessus), servi par le port des personnes.
 *
 * IL EST TOUJOURS COMPLET : le port rend l'annuaire ENTIER (quelques dizaines
 * de personnes, sans plafond), donc un identifiant qu'il ne connait pas n'est
 * a personne, et la case dit `REPLI_PERSONNE_INCONNUE` plutot que l'id.
 */
function demanderAnnuaireDePersonnes(port: PersonnesPort, reference: ColumnReference): void {
  const cle = cleDeJeu(reference.family, reference.type);
  const courant = MAGASIN.get(cle);
  if (courant !== undefined && courant.revision === REVISION) return;
  const revision = REVISION;
  MAGASIN.set(cle, {
    revision,
    statut: courant?.statut === 'ready' ? 'ready' : 'loading',
    annuaire: courant?.annuaire ?? ANNUAIRE_VIDE,
  });
  prevenir();

  lirePersonnes(port, revision).then(
    (personnes) => {
      if (MAGASIN.get(cle)?.revision !== revision) return;
      MAGASIN.set(cle, {
        revision,
        statut: 'ready',
        annuaire: {
          fiches: fichesDAnnuaireDePersonnes(
            personnes,
            reference.type === TYPE_ANNUAIRE_PAR_UID,
            reference,
          ),
          complet: true,
        },
      });
      prevenir();
    },
    (cause: unknown) => {
      if (MAGASIN.get(cle)?.revision !== revision) return;
      const error = cause instanceof Error ? cause : new Error(String(cause));
      console.error(`[taki/personnes] Annuaire des personnes "${cle}" illisible.`, error);
      MAGASIN.set(cle, { revision, statut: 'error', annuaire: ANNUAIRE_VIDE, error });
      prevenir();
    },
  );
}

/**
 * L'HOTE NE FOURNIT PAS DE PORT : ON LE DIT, on ne fait pas semblant d'attendre.
 *
 * C'est le cas d'un banc d'essai, d'un test ou d'un shell sans SDK. Sans cette
 * marque, l'annuaire resterait « en chargement » POUR TOUJOURS, et le selecteur
 * de fiche liee tournerait sur un squelette que rien n'arrete : un controle mort
 * et muet, exactement ce que G16 interdit. La raison est celle de la carte liee,
 * ecrite une fois (`RAISON_LIENS.sansPort`).
 *
 * `revision: -1` : cette marque n'appartient a AUCUNE revision, donc la premiere
 * vraie demande la remplace. Un hote qui monte son port plus tard n'est pas
 * condamne par un rendu ou il ne l'avait pas encore.
 */
const SANS_PORT = new Error(RAISON_LIENS.sansPort);

/** La meme marque pour l'annuaire des personnes, avec SA raison. */
const SANS_PORT_PERSONNES = new Error(RAISON_PERSONNES.sansPort);

function marquerSansPort(reference: ColumnReference, raison: Error): void {
  const cle = cleDeJeu(reference.family, reference.type);
  const courant = MAGASIN.get(cle);
  /* ON NE TOUCHE A RIEN DANS TROIS CAS, et le troisieme a ete ajoute apres
     relecture : deja charge par un ecran qui avait un port (un annuaire lu
     reste lisible, meme sur une surface sans port), deja marque, ou UNE LECTURE
     EST EN VOL. Ce dernier cas est reel : une surface sans port montee pendant
     qu'un ecran charge (un banc d'essai a cote d'un ecran, un test) ecrasait la
     demande en cours par une erreur, et la reponse qui revenait ensuite etait
     jetee comme perimee. L'annuaire restait « illisible » pour toujours. */
  if (courant?.statut === 'ready' || courant?.statut === 'loading') return;
  if (courant?.error === raison) return;
  MAGASIN.set(cle, { revision: -1, statut: 'error', annuaire: ANNUAIRE_VIDE, error: raison });
  prevenir();
}

/** Aucun id voulu : une identite stable, pour que l'effet ne reparte pas. */
const AUCUN_ID_VOULU: readonly IdsVoulus[] = [];

/**
 * LES ANNUAIRES DES REFERENCES DEMANDEES : ce qui est PRET, et rien d'autre.
 *
 * Le hook declenche les chargements qui manquent et rend la carte de ce qui est
 * arrive. Une reference en cours de PREMIER chargement est ABSENTE de la carte,
 * et c'est prevu : la projection montre alors l'id nu, exactement comme les
 * ecrans d'avant ce lot, qui ne passent aucun annuaire.
 *
 * SANS PORT, RIEN NE SE CHARGE ET RIEN NE CASSE : un banc d'essai, un test ou un
 * shell sans SDK montrent des ids, et le selecteur de la fiche DIT pourquoi
 * (`marquerSansPort`). Une app montee sans hote n'est pas cassee, elle est
 * incomplete, et ca se lit.
 */
export function useAnnuaires(
  port: LiensPort | null,
  references: readonly ColumnReference[],
  /**
   * LE PORT DES PERSONNES (`usePersonnesPort()`). TOUS LES APPELANTS LE
   * PASSENT : le moteur d'ecran, la carte liee, le selecteur de rattachement.
   * Une surface qui l'oublierait marquerait l'annuaire des personnes « sans
   * port » et son choix de personne resterait grise, alors que l'hote le
   * fournit. Il n'a pas de valeur par defaut pour que le compilateur le
   * rappelle a chaque appel.
   */
  personnes: PersonnesPort | null,
  /**
   * LES IDS QUE LA SURFACE MONTRE (`idsDesReferences`), pour nommer ceux que la
   * fenetre ne couvre pas. Facultatif, et A MEMOISER par l'appelant : son
   * identite relance l'effet.
   */
  voulus: readonly IdsVoulus[] = AUCUN_ID_VOULU,
): Annuaires {
  const annuaires = useSyncExternalStore(sAbonner, lireInstantane, lireInstantane);
  /* LA REVISION EST LUE AU RENDU, et elle est une DEPENDANCE de l'effet :
     `invaliderAnnuaires` previent les abonnes, donc ce composant se rend, donc
     l'effet repart et redemande ce qui vient d'etre perime. Sans elle, une
     invalidation n'aurait recharge que les annuaires dont le CONTENU a change,
     c'est-a-dire aucun. */
  const revision = useSyncExternalStore(sAbonner, lireRevision, lireRevision);
  /* LES CLEFS, ET PAS LE TABLEAU : `references` est refabrique a chaque rendu
     par l'appelant (les colonnes sont assemblees a chaque passage), donc une
     dependance sur son identite relancerait l'effet sans fin. Ce qui decide
     vraiment d'une lecture, ce sont les JEUX vises. */
  const clefs = useMemo(
    () => references.map((reference) => cleDeJeu(reference.family, reference.type)).join('|'),
    [references],
  );
  /* Les references du rendu courant, lues par l'effet sans entrer dans ses
     dependances (voir les clefs juste au-dessus). Meme patron que la carte
     liee avec `carteRef`. */
  const referencesRef = useRef(references);
  referencesRef.current = references;

  useEffect(() => {
    /* Chaque reference part vers SON port, et un port absent marque la
       sienne seulement (`demanderAnnuaire`). */
    for (const reference of referencesRef.current) demanderAnnuaire(port, reference, personnes);
    // `clefs` et `revision` decident ; les references ne sont lues que dedans.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [port, personnes, clefs, revision]);

  /* APRES la demande des fenetres : les ids voulus s'inscrivent, et partent des
     que la fenetre de leur jeu est revenue. */
  useEffect(() => {
    for (const { reference, ids } of voulus) souhaiterTitres(port, reference, ids);
  }, [port, voulus]);

  return annuaires;
}

/**
 * LE MAGASIN, REMIS A NEUF. Reserve aux TESTS : deux tests qui partagent un
 * cache de module se contaminent, et le second passerait (ou echouerait) pour
 * une raison ecrite dans le premier.
 */
export function viderAnnuairesPourTest(): void {
  MAGASIN.clear();
  COMPLEMENTS.clear();
  LECTURE_PERSONNES = null;
  REVISION = 0;
  prevenir();
}
